import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Wine, MapPin, Camera, Coffee, AlertTriangle } from 'lucide-react';
import './GuidePage.css';

export default function WhiskyTrailStory() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🥃 Whisky Trail per Rad - Anna's Genuss-Tour</h1>
        
        <div className="info-box">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div>
              <User size={24} />
            </div>
            <div>
              <p><strong>Autorin:</strong> Anna B., 38 Jahre</p>
              <p><strong>Tour:</strong> Speyside Whisky Trail</p>
              <p><strong>Dauer:</strong> 5 Tage (Juli 2024)</p>
              <p><strong>Distanz:</strong> 180 km, ~2.000 Höhenmeter</p>
              <p><strong>Rad:</strong> Touring-Bike (kein E-Bike - Fehler! 😅)</p>
            </div>
          </div>
        </div>

        <section>
          <h2>Warum Whisky Trail?</h2>
          <p>
            Ich bin Whisky-Fan und Radfahrerin - warum nicht beides kombinieren? <strong>Speyside 
            ist DAS Whisky-Herz Schottlands</strong>: Über 50 Destillerien in einer Region! 
            Glenfiddich, Macallan, Glenlivet, Balvenie... die Namen allein! 🥃
          </p>
          <p>
            <strong>Die Herausforderung:</strong> Wie kombiniert man Whisky-Verkostungen mit 
            Radfahren? Spoiler: Mit Planung und Selbstdisziplin! (Und ja, ich hab ein paar 
            Lektionen gelernt... 😅)
          </p>
        </section>

        <section>
          <h2>Die Planung</h2>
          
          <h3>Destillerien vorbuchen!</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Wichtig:</strong> Destillery Tours müssen oft 2-4 Wochen vorher gebucht 
            werden! Spontan = oft ausgebucht (besonders Macallan, Glenfiddich).
          </div>

          <h3>Mein Etappenplan</h3>
          <div className="info-box">
            <ul>
              <li><strong>Tag 1:</strong> Elgin → Dufftown (50km, 600m HM) - Glenfiddich Tour</li>
              <li><strong>Tag 2:</strong> Dufftown Loop (40km, 400m HM) - Balvenie + Aberlour</li>
              <li><strong>Tag 3:</strong> Dufftown → Glenlivet (60km, 700m HM) - Glenlivet Tour</li>
              <li><strong>Tag 4:</strong> Glenlivet → Craigellachie (40km, 300m HM) - Macallan Tour</li>
              <li><strong>Tag 5:</strong> Craigellachie → Elgin zurück (50km, 500m HM)</li>
            </ul>
          </div>

          <h3>Die Regel: MAX 2 Verkostungen pro Tag!</h3>
          <p>
            Ich hatte anfangs geplant: 3-4 Destillerien pro Tag. <strong>Realität:</strong> 
            Nach 2 Verkostungen (je 5-7 Drams = kleine Schlucke), bin ich "warm" genug. 
            Mehr = unverantwortlich auf dem Rad!
          </p>
        </section>

        <section>
          <h2>Tag 1: Elgin → Dufftown - Der Start</h2>
          
          <p>
            <strong>Elgin:</strong> Startpunkt, hübsche Kleinstadt, Elgin Cathedral (Ruine). 
            Ich checkte im Hotel ein, ließ Gepäck dort (komme zurück), und startete mit leichtem 
            Rucksack.
          </p>

          <h3>Glen Moray Destillerie (Elgin)</h3>
          <ul>
            <li><strong>Tour:</strong> 10 Uhr (vor Abfahrt!)</li>
            <li><strong>Dauer:</strong> 1.5h inkl. Verkostung</li>
            <li><strong>Verkostung:</strong> 3 Drams (Classic, 12 Year, Cask Finish)</li>
            <li><strong>Kosten:</strong> £15</li>
            <li><strong>Tipp:</strong> <em>Spit & Spit</em> - probieren, nicht schlucken! 
            (Hat anfangs nicht geklappt... 😅)</li>
          </ul>

          <h3>Fahrt nach Dufftown</h3>
          <p>
            50km durch Speyside-Landschaft = wunderschön! Grüne Hügel, Flüsse, Distillery-Türme 
            überall (charakteristische Kupfer-Pagoden). <strong>Aber: Welliger als erwartet!</strong> 
            600 Höhenmeter fühlten sich an wie 1.000.
          </p>

          <h3>Glenfiddich Destillerie (Dufftown)</h3>
          <div className="pro-tip">
            <h3><Wine size={20} /> Glenfiddich - Die Legende</h3>
            <p>
              Glenfiddich = meistverkaufter Single Malt weltweit! Tour ist professionell, Museum 
              toll, Verkostung großzügig (5 Drams!).
            </p>
            <ul>
              <li><strong>Tour:</strong> 16 Uhr (letzter Slot - perfekt nach Radeln!)</li>
              <li><strong>Highlight:</strong> Warehouse Visit (Fässer probieren direkt!)</li>
              <li><strong>Kosten:</strong> £25 (Explorer Tour)</li>
            </ul>
          </div>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Lerneffekt Tag 1:</strong> 2 Verkostungen + 50km Radeln = ich war PLATT! 
            Abendessen, und direkt ins Bett. Kein Pub-Besuch mehr. 😴
          </div>

          <p><strong>Übernachtung:</strong> Dufftown B&B (Whisky-Capital! 9 Destillerien in 20km!)</p>
        </section>

        <section>
          <h2>Tag 2: Dufftown Loop - Die Intensiv-Tour</h2>
          
          <p>
            Heute: Kurze Distanz (40km), aber <strong>2 Destillery Tours + viel Whisky!</strong>
          </p>

          <h3>Balvenie Destillerie (9:30 Uhr)</h3>
          <ul>
            <li><strong>Besonderheit:</strong> Sehr traditionell, noch eigene Malting Floor!</li>
            <li><strong>Tour:</strong> Klein & persönlich (max. 10 Personen)</li>
            <li><strong>Verkostung:</strong> 4 Drams (12 Year, Doublewood, Caribbean Cask, 17 Year)</li>
            <li><strong>Mein Favorit:</strong> Caribbean Cask (smooth & fruchtig!)</li>
            <li><strong>Kosten:</strong> £35</li>
          </ul>

          <h3>Mittagspause: The Whisky Shop (Dufftown)</h3>
          <p>
            <strong>Realisation:</strong> Nach 2 Verkostungen am Vormittag war ich "angeheitert". 
            Ich aß Fish & Chips, trank VIEL Wasser, wartete 2 Stunden. <strong>Verantwortung!</strong>
          </p>

          <h3>Aberlour Destillerie (15 Uhr)</h3>
          <ul>
            <li><strong>Lage:</strong> Am River Spey (malerisch!)</li>
            <li><strong>Tour:</strong> Standard Tour (billiger als Glenfiddich)</li>
            <li><strong>Verkostung:</strong> 3 Drams (10 Year, 16 Year Double Cask, A'bunadh)</li>
            <li><strong>A'bunadh:</strong> Cask Strength (60% ABV!) - <em>WOW!</em> Stark aber lecker!</li>
            <li><strong>Kosten:</strong> £20</li>
          </ul>

          <div className="pro-tip">
            <h3><Coffee size={20} /> Spit & Spit Strategie</h3>
            <p>
              Bei Verkostungen: <strong>Probieren, nicht trinken!</strong> Profis machen das so:
            </p>
            <ul>
              <li>Kleinen Schluck nehmen (1-2ml)</li>
              <li>Im Mund bewegen (Geschmack aufnehmen)</li>
              <li>Schlucken ODER ausspucken (Spittoon ist da!)</li>
              <li>Nach jedem Dram: Wasser trinken + Cracker essen</li>
            </ul>
            <p>
              <strong>Mein Fehler:</strong> Tag 1 alles geschluckt. Tag 2: Gelernt! Spuckte 50% 
              aus (fühlt sich komisch an, aber ist OK!).
            </p>
          </div>

          <p><strong>Abend:</strong> Dufftown, Pub-Dinner, KEIN Whisky mehr! 😅</p>
        </section>

        <section>
          <h2>Tag 3: Dufftown → Glenlivet - Die Königsetappe</h2>
          
          <p>
            60km, 700 Höhenmeter, <strong>durch einsame Highlands</strong> nach Glenlivet. 
            Landschaftlich schönste Etappe!
          </p>

          <h3>Die Fahrt</h3>
          <p>
            A941 → B9009 → Glenlivet. <strong>Kaum Verkehr, grüne Hügel, Schafe, Einsamkeit.</strong> 
            Ich liebte es! Nur: Die 700 Höhenmeter taten weh (kein E-Bike = schwitzen! 💦).
          </p>

          <h3>Glenlivet Destillerie</h3>
          <div className="info-box">
            <h4><Wine size={20} /> The Glenlivet - Der Pionier</h4>
            <p>
              Glenlivet war die erste legale Destillerie Schottlands (1824)! Historisch wichtig, 
              heute: Riesiger Betrieb (gehört zu Pernod Ricard).
            </p>
            <ul>
              <li><strong>Tour:</strong> 14 Uhr (Premium Tour gebucht)</li>
              <li><strong>Highlight:</strong> Cask Warehouse (kühle 12°C, Engel's Share riechen!)</li>
              <li><strong>Verkostung:</strong> 5 Drams (Founder's Reserve, 12, 15, 18, Nadurra)</li>
              <li><strong>Favorit:</strong> 18 Year (smooth, komplex, teuer! £120/Flasche 😱)</li>
              <li><strong>Kosten:</strong> £40 (Premium Tour)</li>
            </ul>
          </div>

          <p><strong>Übernachtung:</strong> Glenlivet (nur 1 Hotel! Mitten im Nirgendwo!)</p>
        </section>

        <section>
          <h2>Tag 4: Glenlivet → Craigellachie - Macallan!</h2>
          
          <p>
            40km entspannt nach Craigellachie. <strong>Heute: THE BIG ONE = Macallan!</strong>
          </p>

          <h3>The Macallan Distillery</h3>
          <div className="warning-box">
            <Wine size={24} />
            <strong>Macallan = LUXUS!</strong><br />
            Neue Destillerie (2018, £140 Millionen!), futuristisches Design, teuerste Whiskys 
            Schottlands. Tour = wie Apple Store für Whisky! 😂
          </div>

          <ul>
            <li><strong>Tour:</strong> 11 Uhr (The Macallan Journey - £50!)</li>
            <li><strong>Design:</strong> Gebäude ist in Hügel gebaut (unsichtbar von oben!)</li>
            <li><strong>Verkostung:</strong> 4 Drams in separatem Tasting Room (edel!)</li>
            <li><strong>Whiskys:</strong> Double Cask 12, Sherry Oak 12, Rare Cask, Reflexion</li>
            <li><strong>Reflexion:</strong> £500/Flasche (!) - ich probierte für £0 dank Tour! 🎉</li>
          </ul>

          <div className="pro-tip">
            <h3><Camera size={20} /> Foto-Tipp</h3>
            <p>
              Macallan Gebäude von oben fotografieren (Easter Elchies House Hügel). Das Dach ist 
              wellenförmig & begrünt - sieht aus wie Hobbiton! <strong>Instagram-Gold!</strong>
            </p>
          </div>

          <h3>Craigellachie</h3>
          <p>
            Winziges Dorf, aber: <strong>The Highlander Inn</strong> = 1.000+ Whiskys! 
            Ich verbrachte den Abend dort, probierte 5 weitere Drams (ja, ich weiß... 😅), 
            und quatschte mit Locals über Whisky. <strong>Perfekter Tag!</strong>
          </p>

          <p><strong>Übernachtung:</strong> Craigellachie Hotel (teuer, aber schön!)</p>
        </section>

        <section>
          <h2>Tag 5: Craigellachie → Elgin - Das Finale</h2>
          
          <p>
            50km zurück nach Elgin. <strong>Keine Destillery mehr</strong> (genug Whisky! 😂), 
            nur entspannt radeln & reflektieren.
          </p>

          <h3>Strathisla Distillery (Keith)</h3>
          <p>
            OK, ich log: EINE Destillerie noch! 😅 Strathisla = schönste Destillerie Schottlands 
            (Architektur!). Chivas Regal Homebase.
          </p>
          <ul>
            <li><strong>Tour:</strong> Kurz (1h), günstig (£15)</li>
            <li><strong>Besonderheit:</strong> Älteste Destillerie Highlands (1786!)</li>
            <li><strong>Verkostung:</strong> 2 Drams (Strathisla 12, Chivas Regal 18)</li>
          </ul>

          <p><strong>Zurück in Elgin:</strong> Abschied-Dinner, Zusammenfassung, Kopfschmerzen 
          (zu viel Whisky diese Woche! 🤕)</p>
        </section>

        <section>
          <h2>Was ich über Whisky Trail gelernt habe</h2>
          
          <h3>Die 10 Whisky-Trail-Learnings</h3>
          <ol>
            <li><strong>MAX 2 Destillerien pro Tag!</strong> Mehr = unverantwortlich + du schmeckst 
            nichts mehr (Palate-Overload!)</li>
            <li><strong>Spit & Spit ist OK!</strong> Profis machen das, du darfst auch. Kein Schande!</li>
            <li><strong>Wasser trinken!</strong> Nach jedem Dram: Wasser! Sonst: Dehydration + 
            Kopfschmerzen.</li>
            <li><strong>Essen zwischen Tours!</strong> Whisky auf leeren Magen = BAD IDEA! 
            Fish & Chips = Freund!</li>
            <li><strong>Pause nach Verkostung!</strong> Mind. 1-2h warten bis Radfahren. 
            Verantwortung!</li>
            <li><strong>Vorbuchen!</strong> Macallan, Glenfiddich, Glenlivet = oft ausgebucht!</li>
            <li><strong>E-Bike wäre besser gewesen!</strong> 2.000 Höhenmeter + Whisky = ich war 
            oft müde. E-Bike = weniger Stress!</li>
            <li><strong>Speyside im Juli = busy!</strong> Viele Touristen. Frühling/Herbst = 
            ruhiger (next time!).</li>
            <li><strong>Highlander Inn (Craigellachie) = Pflicht!</strong> 1.000+ Whiskys, 
            tolle Atmosphäre!</li>
            <li><strong>Genieße die Landschaft!</strong> Es geht nicht nur um Whisky - Speyside 
            ist wunderschön!</li>
          </ol>
        </section>

        <section>
          <h2>Kosten Whisky Trail</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <strong>Destillery Tours (8):</strong> £200
            </div>
            <div className="data-item">
              <strong>Unterkünfte (5 Nächte):</strong> £450 (B&Bs + Hotels)
            </div>
            <div className="data-item">
              <strong>Essen & Pubs:</strong> £250
            </div>
            <div className="data-item">
              <strong>Whisky-Käufe:</strong> £180 (6 Flaschen als Souvenir 😅)
            </div>
          </div>
          <p><strong>Gesamt:</strong> ~£1.080 (~€1.250) für 5 Tage</p>
          <p><em>(Flug nicht eingerechnet)</em></p>
        </section>

        <section>
          <h2>Die Top 5 Destillerien (meine Meinung!)</h2>
          
          <ol>
            <li><strong>Macallan:</strong> Luxus, Design, teuerste Verkostung - aber lohnt sich!</li>
            <li><strong>Glenfiddich:</strong> Professionell, gute Tour, Warehouse-Visit = cool!</li>
            <li><strong>Balvenie:</strong> Traditionell, klein, persönlich - authentisch!</li>
            <li><strong>Glenlivet:</strong> Historisch wichtig, große Anlage, gute Verkostung</li>
            <li><strong>Strathisla:</strong> Schönste Architektur, kurze Tour, entspannt</li>
          </ol>

          <p><em>(Aberlour war auch gut, aber nicht Top 5)</em></p>
        </section>

        <section>
          <h2>Würde ich es nochmal machen?</h2>
          
          <p>
            <strong>JA! Aber mit Änderungen:</strong>
          </p>
          <ul>
            <li><strong>E-Bike statt normales Rad!</strong> 2.000 Höhenmeter + Whisky = müde!</li>
            <li><strong>6 Tage statt 5:</strong> Mehr Zeit, weniger Stress</li>
            <li><strong>Frühling (Mai) statt Sommer:</strong> Weniger Touristen</li>
            <li><strong>Nur 1 Verkostung pro Tag:</strong> Qualität > Quantität (lernte ich zu spät!)</li>
          </ul>

          <h3>Mein Fazit</h3>
          <p>
            Whisky Trail war <strong>Genuss pur!</strong> Ja, ich hab zu viel Whisky getrunken 
            (Kopfschmerzen Tag 4-5 😅). Ja, ich war manchmal zu müde zum Radeln. Aber: 
            <strong>Die Kombination aus Landschaft, Kultur, Genuss und Sport = perfekt!</strong>
          </p>
          <p>
            <strong>Wichtig:</strong> Verantwortung! Whisky & Radeln = Vorsicht! Spit & Spit nutzen, 
            Pausen machen, Wasser trinken. Dann ist es sicher UND genussvoll!
          </p>
          <p>
            <strong>Slàinte Mhath!</strong> (Gaelic für "Cheers!") 🥃
          </p>
        </section>

        <div className="pro-tip">
          <h3>🎯 Anna's Top 5 Whisky-Momente</h3>
          <ol>
            <li><strong>Macallan Reflexion probieren:</strong> £500/Flasche für £0 dank Tour!</li>
            <li><strong>Glenfiddich Warehouse:</strong> Fass-Verkostung direkt im Lager</li>
            <li><strong>Highlander Inn Abend:</strong> 1.000+ Whiskys, Locals, tolle Gespräche</li>
            <li><strong>Aberlour A'bunadh:</strong> Cask Strength (60% ABV) - WOW!</li>
            <li><strong>Speyside Landschaft:</strong> Grüne Hügel, Flüsse, Einsamkeit - perfekt!</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Hilfreiche Guides</h4>
          <ul>
            <li><Link to="/wissen/region/highlands">🏔️ Highlands-Guide</Link> - Speyside Details</li>
            <li><Link to="/wissen/verkehrsregeln">🚦 Verkehrsregeln</Link> - Alkohol am Lenker!</li>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Was du brauchst</li>
          </ul>
        </div>
      </article>
    </div>
  );
}
